import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import LiveBoard from './Board';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatTime } from '../utils/dates';

// ─── CSV export ───────────────────────────────────────────────────────────────
// `field` (falls back to `key`) is the row property to read — kept separate
// from `key` so two columns can derive different display values (e.g. a date
// and a time) from the same underlying field without a React key collision.
function exportCSV(rows, cols, filename) {
  const header = cols.map(c => c.label).join(',');
  const body   = rows.map(row =>
    cols.map(c => {
      const v = row[c.field ?? c.key] ?? '';
      return String(v).includes(',') || String(v).includes('"') ? `"${String(v).replace(/"/g,'""')}"` : v;
    }).join(',')
  );
  const blob = new Blob([[header, ...body].join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── PDF export (browser print) ────────────────────────────────────────────────
function groupBy(rows, key) {
  const map = new Map();
  rows.forEach(r => {
    const k = r[key] ?? '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });
  return map;
}

// Injects the print stylesheet once. Everything on the page is hidden when
// printing except the currently-mounted #report-print-view.
function usePrintStyles() {
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'cgs-reports-print-style';
    style.textContent = `
      @media print {
        * { visibility: hidden !important; }
        #report-print-view, #report-print-view * { visibility: visible !important; }
        #report-print-view {
          position: absolute; left: 0; top: 0; width: 100%;
          background: #fff; padding: 0; margin: 0;
        }
        @page { size: A4 portrait; margin: 15mm; }
        #report-print-view table { width: 100%; border-collapse: collapse; }
        #report-print-view th, #report-print-view td {
          border: 1px solid #999; padding: 5px 8px; font-size: 10px; text-align: left; color: #000;
        }
        #report-print-view thead { display: table-header-group; }
        #report-print-view tfoot { display: table-footer-group; }
        #report-print-view tr { page-break-inside: avoid; }
        #report-print-view th {
          background: #dcdcdc !important; font-weight: 700;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        #report-print-view tbody tr:nth-child(even) td {
          background: #f2f2f2 !important;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        #report-print-view tr.subtotal-row td, #report-print-view tr.total-row td {
          background: #eaeaea !important; font-weight: 700;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.getElementById('cgs-reports-print-style')?.remove();
  }, []);
}

// Header block repeated at the top of every printed report.
function ReportPrintHeader({ title, from, to }) {
  const today = new Date().toLocaleDateString('en-US');
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 700 }}>Creative Glass Systems Inc.</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3 }}>{title}</div>
      {(from || to) && <div style={{ fontSize: 10, color: '#333', marginTop: 5 }}>Period: {from || '—'} to {to || '—'}</div>}
      <div style={{ fontSize: 10, color: '#333' }}>Generated: {today}</div>
      <hr style={{ border: 'none', borderTop: '2px solid #000', marginTop: 8, marginBottom: 0 }} />
    </div>
  );
}

// ─── Shared filter bar styles ─────────────────────────────────────────────────
const inp = { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', padding: '7px 11px', fontFamily: 'var(--font-body)', fontSize: 13 };

function DateRange({ from, to, onFrom, onTo }) {
  return (
    <>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={inp} />
      <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>to</span>
      <input type="date" value={to}   onChange={e => onTo(e.target.value)}   style={inp} />
    </>
  );
}

function defaultRange() {
  const to   = new Date().toISOString().slice(0,10);
  const from = new Date(Date.now() - 30*86400000).toISOString().slice(0,10);
  return { from, to };
}

// ─── Reusable report table ────────────────────────────────────────────────────
function ReportTable({ rows, cols, loading, emptyMsg = 'No data for the selected filters' }) {
  if (loading) return <div className="loading">Loading…</div>;
  if (!rows.length) return <div className="empty-state">{emptyMsg}</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c.key} style={c.style?.(row) || {}}>
                  {c.fmt ? c.fmt(row[c.field ?? c.key], row) : (row[c.field ?? c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Report header row ────────────────────────────────────────────────────────
function ReportHeader({ title, rows, cols, filename, onExportPDF, printing, children }) {
  return (
    <div style={{ display: printing ? 'none' : 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>{title}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => exportCSV(rows, cols, filename)}
          disabled={!rows.length}
        >
          ↓ Export CSV
        </button>
        {onExportPDF && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={onExportPDF}
            disabled={!rows.length}
          >
            ⎙ Export PDF
          </button>
        )}
      </div>
    </div>
  );
}

// ─── 1. Production Hours ──────────────────────────────────────────────────────
// Session-level rows (one per clock session) so real start/end times can show.
function ProductionHoursPrint({ rows, from, to }) {
  const groups = groupBy(rows, 'staff_name');
  const grandTotal = rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);
  return (
    <div id="report-print-view" style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#000', background: '#fff' }}>
      <ReportPrintHeader title="Production Hours" from={from} to={to} />
      <table>
        <thead>
          <tr>
            <th>Date</th><th>Staff</th><th>Project</th><th>Job No</th>
            <th>Start</th><th>End</th>
            <th style={{ textAlign: 'right' }}>Hours</th>
          </tr>
        </thead>
        <tbody>
          {[...groups.entries()].map(([staffName, group]) => {
            const subtotalHours = group.reduce((s, r) => s + parseFloat(r.hours || 0), 0);
            return (
              <React.Fragment key={staffName}>
                {group.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.started_at)}</td>
                    <td>{staffName}</td>
                    <td>{r.project_title}</td>
                    <td>{r.job_number}</td>
                    <td>{formatTime(r.started_at)}</td>
                    <td>{formatTime(r.ended_at)}</td>
                    <td style={{ textAlign: 'right' }}>{r.hours}</td>
                  </tr>
                ))}
                <tr className="subtotal-row">
                  <td colSpan={6}>Subtotal — {staffName}</td>
                  <td style={{ textAlign: 'right' }}>{subtotalHours.toFixed(2)}</td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td colSpan={6}>Grand Total</td>
            <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ProductionHours({ projects, staff, printing, onPrint }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [projFilter, setProjFilter] = useState('');
  const [sfFilter,   setSfFilter]   = useState('');
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ date_from: from, date_to: to });
    if (projFilter) p.set('project_id', projFilter);
    if (sfFilter)   p.set('staff_id',   sfFilter);
    api.get(`/reports/production-hours?${p}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to, projFilter, sfFilter]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'date',          field: 'started_at', label: 'Date',    fmt: formatDate, style: () => ({ color: 'var(--text-muted)', fontSize: 12 }) },
    { key: 'staff_name',    label: 'Staff' },
    { key: 'project_title', label: 'Project' },
    { key: 'job_number',    label: 'Job No', style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }) },
    { key: 'start',         field: 'started_at', label: 'Start',   fmt: (v, row) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {row.source === 'manual' ? formatDate(v) : formatTime(v)}
          {row.source === 'manual' && <span className="badge badge-manual">Manual</span>}
        </span>
      ), style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'end',           field: 'ended_at',   label: 'End',     fmt: (v, row) => row.source === 'manual' ? '—' : formatTime(v), style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'hours',         label: 'Hours', fmt: v => <strong>{v}</strong> },
  ];

  const total = rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);

  return (
    <div>
      <ReportHeader title="Production Hours" rows={rows} cols={cols} filename={`production-hours-${from}-${to}.csv`} onExportPDF={onPrint} printing={printing}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
        <select value={sfFilter} onChange={e => setSfFilter(e.target.value)} style={inp}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </ReportHeader>
      {!printing && total > 0 && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          {rows.length} rows · <strong style={{ color: 'var(--accent)' }}>{total.toFixed(2)}h</strong> total
        </div>
      )}
      {!printing && <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>}
      {printing && <ProductionHoursPrint rows={rows} from={from} to={to} />}
    </div>
  );
}

// ─── 2. Job Cost ──────────────────────────────────────────────────────────────
function aggregateJobCost(rows) {
  const map = new Map();
  rows.forEach(r => {
    if (!map.has(r.job_number)) {
      map.set(r.job_number, {
        job_number: r.job_number,
        project_title: r.project_title,
        quoted_value: r.quoted_value,
        total_hours: 0,
      });
    }
    map.get(r.job_number).total_hours += parseFloat(r.total_hours || 0);
  });
  return [...map.values()].sort((a, b) =>
    a.job_number.localeCompare(b.job_number, undefined, { numeric: true })
  );
}

function JobCostPrint({ rows }) {
  const jobs = aggregateJobCost(rows);
  return (
    <div id="report-print-view" style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#000', background: '#fff' }}>
      <ReportPrintHeader title="Job Cost" />
      <table>
        <thead>
          <tr>
            <th>Job #</th><th>Project Title</th>
            <th style={{ textAlign: 'right' }}>Quoted Value</th>
            <th style={{ textAlign: 'right' }}>Total Hours</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(j => (
            <tr key={j.job_number}>
              <td>{j.job_number}</td>
              <td>{j.project_title}</td>
              <td style={{ textAlign: 'right' }}>{j.quoted_value != null ? formatCurrency(parseFloat(j.quoted_value)) : '—'}</td>
              <td style={{ textAlign: 'right' }}>{j.total_hours.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobCost({ projects, printing, onPrint }) {
  const [projFilter, setProjFilter] = useState('');
  const [rows,    setRows]    = useState([]);
  const [rate,    setRate]    = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (projFilter) p.set('project_id', projFilter);
    api.get(`/reports/job-cost?${p}`)
      .then(r => { setRows(r.data.value || []); setRate(r.data.hourly_rate || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projFilter]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'job_number',     label: 'Job #', style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }) },
    { key: 'project_title',  label: 'Project' },
    { key: 'status',         label: 'Status', fmt: v => <span className={`badge badge-${v}`}>{v?.replace(/_/g,' ')}</span> },
    { key: 'work_centre',    label: 'Work Centre' },
    { key: 'total_hours',    label: 'Hours', fmt: v => <strong>{v}</strong> },
    { key: 'labour_cost',    label: 'Labour Cost ($)', fmt: v => formatCurrency(parseFloat(v || 0)), style: () => ({ color: 'var(--success)', fontFamily: 'var(--font-mono)' }) },
    { key: 'quoted_value',   label: 'Quoted ($)', fmt: v => v != null ? formatCurrency(parseFloat(v)) : '—', style: () => ({ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }) },
];

  return (
    <div>
      <ReportHeader title="Job Cost" rows={rows} cols={cols} filename="job-cost.csv" onExportPDF={onPrint} printing={printing}>
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
        {rate > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>@ {formatCurrency(parseFloat(rate))}/hr</span>}
      </ReportHeader>
      {!printing && <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>}
      {printing && <JobCostPrint rows={rows} />}
    </div>
  );
}

// ─── 3. Non-Productive Time ───────────────────────────────────────────────────
// Session-level rows against clock_session — real reason + start/end time per session.
function NonProductivePrint({ rows, from, to }) {
  const groups = groupBy(rows, 'reason');
  const grandTotal = rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);
  return (
    <div id="report-print-view" style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#000', background: '#fff' }}>
      <ReportPrintHeader title="Non-Productive Time" from={from} to={to} />
      <table>
        <thead>
          <tr>
            <th>Date</th><th>Staff</th><th>Reason</th><th>Start</th><th>End</th>
            <th style={{ textAlign: 'right' }}>Hours</th>
          </tr>
        </thead>
        <tbody>
          {[...groups.entries()].map(([reason, group]) => {
            const subtotalHours = group.reduce((s, r) => s + parseFloat(r.hours || 0), 0);
            return (
              <React.Fragment key={reason}>
                {group.map((r) => (
                  <tr key={r.started_at + r.staff}>
                    <td>{formatDate(r.log_date)}</td>
                    <td>{r.staff}</td>
                    <td>{reason}</td>
                    <td>{formatTime(r.started_at)}</td>
                    <td>{formatTime(r.ended_at)}</td>
                    <td style={{ textAlign: 'right' }}>{r.hours}</td>
                  </tr>
                ))}
                <tr className="subtotal-row">
                  <td colSpan={5}>Subtotal — {reason}</td>
                  <td style={{ textAlign: 'right' }}>{subtotalHours.toFixed(2)}</td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td colSpan={5}>Grand Total</td>
            <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function NonProductive({ printing, onPrint }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/non-productive?date_from=${from}&date_to=${to}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'log_date', label: 'Date',  fmt: formatDate, style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }) },
    { key: 'staff',    label: 'Staff' },
    { key: 'reason',   label: 'Reason', style: () => ({ color: 'var(--info)' }) },
    { key: 'start',    field: 'started_at', label: 'Start', fmt: formatTime, style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'end',      field: 'ended_at',   label: 'End',   fmt: formatTime, style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'hours',    label: 'Hours', fmt: v => <strong>{v}</strong> },
  ];
  const total = rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);

  return (
    <div>
      <ReportHeader title="Non-Productive Time" rows={rows} cols={cols} filename={`non-productive-${from}-${to}.csv`} onExportPDF={onPrint} printing={printing}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </ReportHeader>
      {!printing && total > 0 && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          {rows.length} rows · <strong style={{ color: 'var(--info)' }}>{total.toFixed(2)}h</strong> non-productive
        </div>
      )}
      {!printing && <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>}
      {printing && <NonProductivePrint rows={rows} from={from} to={to} />}
    </div>
  );
}

// ─── 4. Capacity ─────────────────────────────────────────────────────────────
function CapacityPrint({ daily, from, to }) {
  const totals = daily.reduce((acc, d) => ({
    productive: acc.productive + parseFloat(d.productive_hours || 0),
    nonProductive: acc.nonProductive + parseFloat(d.non_productive_hours || 0),
    total: acc.total + parseFloat(d.total_hours || 0),
  }), { productive: 0, nonProductive: 0, total: 0 });
  return (
    <div id="report-print-view" style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#000', background: '#fff' }}>
      <ReportPrintHeader title="Capacity" from={from} to={to} />
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th style={{ textAlign: 'right' }}>Productive Hours</th>
            <th style={{ textAlign: 'right' }}>Non-Productive Hours</th>
            <th style={{ textAlign: 'right' }}>Total Hours</th>
          </tr>
        </thead>
        <tbody>
          {daily.map(d => (
            <tr key={d.log_date}>
              <td>{formatDate(d.log_date)}</td>
              <td style={{ textAlign: 'right' }}>{parseFloat(d.productive_hours || 0).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{parseFloat(d.non_productive_hours || 0).toFixed(2)}</td>
              <td style={{ textAlign: 'right' }}>{parseFloat(d.total_hours || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td>Totals</td>
            <td style={{ textAlign: 'right' }}>{totals.productive.toFixed(2)}</td>
            <td style={{ textAlign: 'right' }}>{totals.nonProductive.toFixed(2)}</td>
            <td style={{ textAlign: 'right' }}>{totals.total.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Capacity({ printing, onPrint }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to,   setTo]   = useState(dr.to);
  const [rows,    setRows]    = useState([]);
  const [daily,   setDaily]   = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/capacity?date_from=${from}&date_to=${to}`)
      .then(r => { setRows(r.data.value || []); setDaily(r.data.daily || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'log_date',    label: 'Date',        fmt: formatDate, style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
    { key: 'day_name',    label: 'Day',         style: () => ({ color: 'var(--text-muted)', fontSize: 12 }) },
    { key: 'work_centre', label: 'Work Centre' },
    { key: 'total_hours', label: 'Hours',       fmt: v => <strong>{v}</strong> },
    { key: 'staff_count', label: 'Staff',       style: () => ({ fontFamily: 'var(--font-mono)', fontSize: 12 }) },
  ];

  return (
    <div>
      <ReportHeader title="Capacity" rows={rows} cols={cols} filename={`capacity-${from}-${to}.csv`} onExportPDF={onPrint} printing={printing}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </ReportHeader>
      {!printing && <div className="card"><ReportTable rows={rows} cols={cols} loading={loading} /></div>}
      {printing && <CapacityPrint daily={daily} from={from} to={to} />}
    </div>
  );
}

// ─── Reports main page ────────────────────────────────────────────────────────
function ReportsInner() {
  const [tab, setTab] = useState('board');
  const [projects,    setProjects]    = useState([]);
  const [allStaff,    setAllStaff]    = useState([]);
  const [printing,    setPrinting]    = useState(false);

  usePrintStyles();

  useEffect(() => {
    const handler = () => setPrinting(false);
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, []);

  const onPrint = () => {
    setPrinting(true);
    setTimeout(() => window.print(), 50);
  };

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.value || []));
    api.get('/staff/all').then(r => setAllStaff(r.data.value || []));
  }, []);

  const tabs = [
    { id: 'board',        label: '◉ Live Board' },
    { id: 'hours',        label: '⏱ Prod. Hours' },
    { id: 'jobcost',      label: '$ Job Cost' },
    { id: 'nonprod',      label: '↻ Non-Productive' },
    { id: 'capacity',     label: '▦ Capacity' },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: printing ? 'none' : undefined }}>
        <h2 className="page-title">RE<span>PORTS</span></h2>
      </div>

      {/* Tab nav */}
      <div style={{ display: printing ? 'none' : 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 16px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            border:     tab === t.id ? '1px solid var(--accent)' : '1px solid var(--border)',
            background: tab === t.id ? 'var(--accent-glow)'      : 'var(--bg-card)',
            color:      tab === t.id ? 'var(--accent)'            : 'var(--text-secondary)',
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
            letterSpacing: 1, textTransform: 'uppercase',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'board'    && <div className="card"><LiveBoard showForceOff={true} /></div>}
      {tab === 'hours'    && <ProductionHours projects={projects} staff={allStaff} printing={printing} onPrint={onPrint} />}
      {tab === 'jobcost'  && <JobCost projects={projects} printing={printing} onPrint={onPrint} />}
      {tab === 'nonprod'  && <NonProductive printing={printing} onPrint={onPrint} />}
      {tab === 'capacity' && <Capacity printing={printing} onPrint={onPrint} />}
    </div>
  );
}

export default function Reports() {
  return <ReportsInner />;
}
